// src/pages/student-portal/courses/aiInstructorUtils.js
// AI Instructor utilities - ported from customer-dashboard (TypeScript → JS)
// Services, persistence, default scripts, helpers

import axiosInstance from '../../../utils/axiosInstance';

// ============================================================================
// AI INSTRUCTOR PERSISTENCE (localStorage)
// ============================================================================

export const aiInstructorPersistence = {
    getWelcomeSeenKey: (courseId) => `ai_instructor_welcome_seen_${courseId}`,
    getModuleIntroSeenKey: (courseId, moduleId) => `ai_instructor_module_intro_seen_${courseId}_${moduleId}`,

    hasSeenWelcome: (courseId) => {
        return localStorage.getItem(aiInstructorPersistence.getWelcomeSeenKey(courseId)) === 'true';
    },

    markWelcomeSeen: (courseId) => {
        localStorage.setItem(aiInstructorPersistence.getWelcomeSeenKey(courseId), 'true');
    },

    hasSeenModuleIntro: (courseId, moduleId) => {
        return localStorage.getItem(aiInstructorPersistence.getModuleIntroSeenKey(courseId, moduleId)) === 'true';
    },

    markModuleIntroSeen: (courseId, moduleId) => {
        localStorage.setItem(aiInstructorPersistence.getModuleIntroSeenKey(courseId, moduleId), 'true');
    },

    clearCourseData: (courseId) => {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.includes('ai_instructor_') && key.includes(courseId)) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
    },

    clearAllData: () => {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('ai_instructor_')) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
    },
};

// ============================================================================
// AI INSTRUCTOR SERVICE
// ============================================================================

export const aiInstructorService = {
    async generateScript({ chatbot_id, script_type, context, generate_audio }) {
        try {
            const response = await axiosInstance.post('/api/lms/portal/ai-instructor/generate-script', {
                chatbot_id,
                script_type,
                course_id: context?.course_id,
                context,
                generate_audio: generate_audio ?? false,
            });
            if (response.data?.success) {
                const d = response.data.data || response.data;
                return {
                    id: d.id || `script_${Date.now()}`,
                    script_type,
                    script_text: d.script_text || d.text || '',
                    audio_base64: d.audio_base64 || d.audio || null,
                    allow_skip: d.allow_skip ?? true,
                    show_text_transcript: d.show_text_transcript ?? true,
                };
            }
            return null;
        } catch (err) {
            console.warn('[AI Instructor] Generate script failed:', err?.message);
            return null;
        }
    },

    async logInteraction({ chatbot_id, course_id, script_type, action }) {
        try {
            await axiosInstance.post('/api/lms/portal/ai-instructor/log-interaction', {
                chatbot_id,
                course_id,
                script_type,
                action,
            });
        } catch {
            // Silent fail for analytics
        }
    },

    getDefaultScript(scriptType, context = {}) {
        const studentName = context.studentName || context.student_name || 'there';
        const courseTitle = context.courseTitle || context.course_title || 'this course';
        const moduleTitle = context.moduleTitle || context.module_title || 'this module';
        const moduleIndex = context.moduleIndex || context.module_index || 1;
        const totalModules = context.totalModules || context.total_modules || 1;
        const score = context.score || 0;
        const passingScore = context.passingScore || context.passing_score || 70;
        const testTitle = context.testTitle || context.test_title || 'the assessment';
        const questionCount = context.questionCount || context.question_count || 10;

        switch (scriptType) {
            case 'course_welcome':
                return `Welcome, ${studentName}! I'm your AI instructor for "${courseTitle}". I'll be guiding you through this learning journey. Let's get started!`;
            case 'course_intro':
                return `${studentName}, this course "${courseTitle}" will give you practical knowledge and skills. Let's begin!`;
            case 'module_intro':
                if (moduleIndex === 1) {
                    return `Let's begin, ${studentName}! You're starting "${moduleTitle}", the first module of ${totalModules}. Take your time and engage with the content. Let's dive in!`;
                }
                return `Great progress, ${studentName}! You're now starting "${moduleTitle}". This is module ${moduleIndex} of ${totalModules}. Keep up the momentum!`;
            case 'module_complete':
                return `Fantastic work completing "${moduleTitle}", ${studentName}! You've finished module ${moduleIndex} of ${totalModules}. Take a moment to appreciate your progress!`;
            case 'test_intro':
                return `It's time for "${testTitle}", ${studentName}! There are ${questionCount} questions. Read each one carefully. You've prepared well for this — you've got this!`;
            case 'test_pass':
                return `Outstanding, ${studentName}! You passed with ${score}%! That's excellent work. Ready for the next challenge?`;
            case 'test_fail':
                return `Don't be discouraged, ${studentName}. You scored ${score}% but need ${passingScore}% to pass. Review the material and try again. I believe in you!`;
            case 'course_complete':
                return `Congratulations, ${studentName}! You've successfully completed "${courseTitle}"! This is a wonderful achievement. Be proud of what you've accomplished!`;
            case 'encouragement':
                return `You're doing wonderfully, ${studentName}! Keep up the excellent work on "${courseTitle}".`;
            default:
                return `Hello, ${studentName}! I'm here to help you succeed in "${courseTitle}".`;
        }
    },
};

// ============================================================================
// SCRIPT TYPE LABELS
// ============================================================================

export function getScriptTypeLabel(type) {
    switch (type) {
        case 'course_welcome': return 'Welcome';
        case 'course_intro': return 'Course Introduction';
        case 'module_intro': return 'Module Introduction';
        case 'module_complete': return 'Module Complete';
        case 'test_intro': return 'Assessment Briefing';
        case 'test_pass': return 'Congratulations';
        case 'test_fail': return 'Keep Going';
        case 'course_complete': return 'Course Complete';
        default: return 'Message';
    }
}

// ============================================================================
// MARKDOWN → HTML CONVERTER (simplified, safe for dangerouslySetInnerHTML)
// ============================================================================

export function convertMarkdownToHtml(text) {
    if (!text) return '';
    if (text.includes('<p>') || text.includes('<br') || text.includes('<ul>') || text.includes('<ol>')) return text;

    let html = text;
    // Bold
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');
    // Italic
    html = html.replace(/(?<![*_])\*([^*\n]+)\*(?![*_])/g, '<em>$1</em>');
    html = html.replace(/(?<![*_])_([^_\n]+)_(?![*_])/g, '<em>$1</em>');
    // Inline code
    html = html.replace(/`([^`]+)`/g, '<code style="background:#f5f3ff;padding:2px 6px;border-radius:4px;font-size:0.85em;color:#7c3aed;">$1</code>');
    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color:#8b5cf6;text-decoration:underline;" target="_blank" rel="noopener noreferrer">$1</a>');

    const lines = html.split('\n');
    const processedLines = [];
    let listItems = [];
    let listType = '';

    const flushList = () => {
        if (listItems.length > 0) {
            const tag = listType === 'ul' ? 'ul' : 'ol';
            processedLines.push(`<${tag} style="padding-left:1.5em;margin:0.5em 0;">${listItems.map((item) => `<li>${item}</li>`).join('')}</${tag}>`);
            listItems = [];
            listType = '';
        }
    };

    lines.forEach((line) => {
        const trimmed = line.trim();
        const ulMatch = trimmed.match(/^[*\-•]\s+(.+)$/);
        const olMatch = trimmed.match(/^\d+[.)]\s+(.+)$/);

        if (ulMatch) {
            if (listType !== 'ul') { flushList(); listType = 'ul'; }
            listItems.push(ulMatch[1]);
        } else if (olMatch) {
            if (listType !== 'ol') { flushList(); listType = 'ol'; }
            listItems.push(olMatch[1]);
        } else {
            flushList();
            if (trimmed.startsWith('### ')) processedLines.push(`<h3 style="font-size:1rem;font-weight:600;margin:0.75em 0 0.5em;">${trimmed.substring(4)}</h3>`);
            else if (trimmed.startsWith('## ')) processedLines.push(`<h2 style="font-size:1.1rem;font-weight:600;margin:1em 0 0.5em;">${trimmed.substring(3)}</h2>`);
            else if (trimmed.startsWith('# ')) processedLines.push(`<h1 style="font-size:1.2rem;font-weight:700;margin:1em 0 0.5em;">${trimmed.substring(2)}</h1>`);
            else if (trimmed === '') processedLines.push('<br/>');
            else processedLines.push(`<p style="margin:0.3em 0;line-height:1.6;">${trimmed}</p>`);
        }
    });
    flushList();
    return processedLines.join('').replace(/(<br\s*\/?>){3,}/g, '<br/><br/>');
}
